import React, { useEffect, useId, useMemo, useState } from 'react';
import { useDataProvider } from 'react-admin';
import {
  Alert, Autocomplete, Box, Button, Chip, IconButton, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { __t } from '../../../i18n/t';
import { Field, LabeledInput, Seg } from '../../../components/wizardKit/controls';
import { C } from '../../../components/wizardKit/tokens';
import { GiftProductType } from '../../../models';
import ProductTypeSelectionModal from '../../gifts/components/modals/ProductTypeSelectionModal';
import { AsyncAutocomplete, ImageWithFallback } from '../../../components';
import { productNeedsRedemptionPlaces } from '../../gifts/wizardForms/redemptionPlaces';
import { CurrencyInput } from '../../../components/mui/inputs';

/** Tipos de recompensa que um nível de campanha aceita — espelha o validator do backend. */
export type RewardType = 'virtual' | 'existing' | 'fuelDiscount' | 'drawing_number';

/** Local de troca de uma recompensa, com o que o posto recebe ALI. */
export interface RewardPlaceValue {
  placeId: string;
  partnerValue: number | null;
  /** Só UI: o preço veio do catálogo (product_place) e não se digita à mão. */
  hasProductPlace?: boolean;
}

export interface RewardValue {
  type: RewardType;
  places: RewardPlaceValue[];
  productName?: string | null;
  productDescription?: string | null;
  cashValue?: number | null;
  productId?: string | null;
  discountId?: string | null;
  drawingId?: string | null;
  luckyNumberAmount?: number | null;
}

export interface RequirementValue {
  type: 'fillin' | 'accumulated_fillin';
  value: number | null;
  valueType: 'cents' | 'volume';
  fuelIds: string[];
  placeIds: string[];
}

export interface LevelRewardValue {
  rewards: RewardValue[];
  requirement: RequirementValue;
  couponHours: number;
}

interface Option { id: string; name: string; }

interface LevelRewardEditorProps {
  chainId: string;
  value: LevelRewardValue;
  onChange: (patch: Partial<LevelRewardValue>) => void;
}

export const emptyReward = (): LevelRewardValue => ({
  rewards: [],
  requirement: { type: 'fillin', value: null, valueType: 'volume', fuelIds: [], placeIds: [] },
  couponHours: 48,
});

/**
 * Quem precisa de local de troca. Reusa a regra do wizard do /gifts em vez de repeti-la:
 * repetida, ela diverge — e foi o que aconteceu na primeira versão daqui, que exigia local
 * para o cashback (que vale em qualquer local da rede e nunca consulta gift_product_place).
 */
export const rewardNeedsPlaces = (reward: RewardValue) =>
  productNeedsRedemptionPlaces({ type: reward.type as any, cashValue: reward.cashValue ?? null } as any);

/**
 * Locais travados enquanto não há produto: o valor do parceiro em cada local vem do catálogo
 * DELE (product_place), então escolher antes é escolher em cima de um preço que ainda vai mudar.
 */
const placesLocked = (reward: RewardValue) => reward.type === 'existing' && !reward.productId;

export const REWARD_TYPE_LABEL: Record<RewardType, string> = {
  virtual: 'Produto virtual',
  existing: 'Produto do catálogo',
  fuelDiscount: 'Desconto no abastecimento',
  drawing_number: 'Número da sorte',
};

const REWARD_ICON: Record<RewardType, React.ReactNode> = {
  virtual: <CardGiftcardIcon fontSize="small" />,
  existing: <Inventory2Icon fontSize="small" />,
  fuelDiscount: <LocalGasStationIcon fontSize="small" />,
  drawing_number: <ConfirmationNumberIcon fontSize="small" />,
};

// A largura de cada campo é o `width` do LabeledInput/Field (máximo em px, 100% da linha
// quando ela é mais estreita): a tabela de breakpoints daqui virou isso.

// Estilos que vivem debaixo de condicional ou só repetem valores: fora do JSX, porque o
// plugin de i18n embrulha literal de texto dentro de ternário (inclusive valor de `sx`).
// Mesmo tamanho do título do `Ask` do kit: dentro do nível estas são as perguntas.
const SECTION_TITLE_SX = { fontSize: 15, fontWeight: 700, color: C.ink } as const;
const FIELD_LABEL_SX = { fontSize: 12.5, fontWeight: 500, color: C.muted } as const;
// `alignItems`: numa coluna, o padrão `stretch` estica o alternador pela largura toda.
const SEG_COLUMN_SX = { display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' } as const;
const COST_CHIP_SX = { bgcolor: C.violetSoft, color: C.violet, borderColor: 'rgba(68,51,180,.18)', fontWeight: 700 } as const;
const REWARD_ICON_SX = {
  width: 30, height: 30, borderRadius: '8px', flex: 'none', color: C.violet,
  bgcolor: 'rgba(68,51,180,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center',
} as const;
const PLACE_ROW_SX = {
  p: 1.75, mt: 1, bgcolor: C.surface, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end',
} as const;
const PLACE_AUTO_SX = { borderColor: 'rgba(39,80,48,.35)' } as const;
const SECTION_SX = { borderTop: `1px solid ${C.lineSoft}`, pt: 2.5 } as const;
const ALIGN_START = 'flex-start';
// O Alert `info` do MUI é azul-claro; aqui ele usa o violeta da marca, como no desconto v2.
const SENTENCE_SX = {
  bgcolor: C.violetSoft,
  color: C.ink,
  border: '1px solid rgba(68,51,180,.16)',
  maxWidth: 740,
  '& .MuiAlert-icon': { color: C.violet },
} as const;

// Direito ao palpite e giro na roleta têm vitrine e janela próprias (Bolão e Roleta): dentro
// de uma campanha de recuperação eles sairiam de uma janela que não é a deles.
const EXCLUDED_PRODUCT_TYPES = [GiftProductType.PredictionTicket, GiftProductType.SpinTicket];

const LevelRewardEditor: React.FC<LevelRewardEditorProps> = ({ chainId, value, onChange }) => {
  const dataProvider = useDataProvider();
  const uid = useId();
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [places, setPlaces] = useState<Option[]>([]);
  const [fuels, setFuels] = useState<Option[]>([]);
  const [rewardDiscounts, setRewardDiscounts] = useState<Option[]>([]);
  const [drawings, setDrawings] = useState<Option[]>([]);

  useEffect(() => {
    const list = (resource: string, filter: any, map: (row: any) => Option) =>
      dataProvider
        .getList(resource, { pagination: { page: 1, perPage: 999 }, sort: { field: 'name', order: 'ASC' }, filter })
        .then(({ data }: any) => data.map(map))
        .catch(() => [] as Option[]);

    // `chains/chainId` é literal: o dataProvider troca pelo id da rede do localStorage
    // (mesma convenção de PRODUCT em constants.ts e do resto do admin). Os combustíveis
    // e os descontos são globais/por filtro, e ficam nas rotas soltas.
    Promise.all([
      list('chains/chainId/places', {}, (p: any) => ({ id: p.id, name: p.name })),
      list('fuels', {}, (f: any) => ({ id: f.id, name: f.name })),
      list('discounts', { chainId, type: 'reward', isEnabled: true },
        (d: any) => ({ id: d.id, name: d.description ? `${d.identify} — ${d.description}` : d.identify })),
      // Só sorteio dirigido pelo Clube e não arquivado pode receber recompensa — mesmo
      // filtro do DrawingNumberProductForm no /gifts.
      list('chains/chainId/drawings', { isClubDriven: true, isArchived: false, status: 'not-finished' },
        (d: any) => ({ id: d.id, name: d.name })),
    ]).then(([placeList, fuelList, discountList, drawingList]) => {
      setPlaces(placeList); setFuels(fuelList);
      setRewardDiscounts(discountList); setDrawings(drawingList);
    });
  }, [dataProvider, chainId]);

  // O teto congela o MAIOR preço de cada recompensa: um teto que subestima o custo deixa a
  // campanha passar do orçamento sem avisar.
  const totalCost = useMemo(
    () => value.rewards.reduce((sum, reward) => {
      const values = reward.places.map(place => place.partnerValue ?? 0);
      return sum + (values.length ? Math.max(...values) : 0);
    }, 0),
    [value.rewards],
  );

  const addReward = (type: GiftProductType, options?: { cashback?: boolean }) => {
    setTypeModalOpen(false);
    const reward: RewardValue = { type: type as RewardType, places: [] };
    if (type === GiftProductType.Virtual) {
      reward.productName = '';
      if (options?.cashback) { reward.cashValue = null; }
    }
    if (type === GiftProductType.DrawingNumber) { reward.luckyNumberAmount = 1; }
    onChange({ rewards: [...value.rewards, reward] });
  };

  /**
   * Valor automático do catálogo, como no passo de locais do /gifts: se o produto já tem
   * preço cadastrado naquele posto (product_place.partnerPrice), ele manda — o operador não
   * digita, e não há duas verdades sobre quanto o posto recebe.
   */
  const buildPlaces = async (reward: RewardValue, placeIds: string[], productId: string | null) => {
    const anterior = new Map(reward.places.map(place => [place.placeId, place]));

    let catalogo = new Map<string, number>();
    if (reward.type === 'existing' && productId && placeIds.length) {
      try {
        const { data } = await dataProvider.getOne('chains/chainId/products', { id: productId });
        const productPlaces = (data as any)?.places || (data as any)?.productPlaces || [];
        catalogo = new Map(productPlaces
          .filter((pp: any) => pp.partnerPrice != null)
          .map((pp: any) => [pp.placeId, Number(pp.partnerPrice)]));
      } catch { /* sem catálogo: cai no preenchimento manual */ }
    }

    return placeIds.map(placeId => {
      const doCatalogo = catalogo.get(placeId);
      if (doCatalogo != null) { return { placeId, partnerValue: doCatalogo, hasProductPlace: true }; }
      return { placeId, partnerValue: anterior.get(placeId)?.partnerValue ?? null, hasProductPlace: false };
    });
  };

  const applyPlaces = async (index: number, placeIds: string[]) => {
    const reward = value.rewards[index];
    updateReward(index, { places: await buildPlaces(reward, placeIds, reward.productId ?? null) });
  };

  /**
   * Trocar o produto troca os preços de catálogo, e limpá-lo limpa os locais — travado o campo,
   * eles ficariam presos embaixo sem como tirar. Produto e locais saem no MESMO updateReward:
   * dois em sequência partem ambos do `value` deste render, e o segundo desfaz o primeiro.
   */
  const applyProduct = async (index: number, option: Option | null) => {
    const reward = value.rewards[index];
    const productId = option?.id ?? null;
    const placeIds = productId ? reward.places.map(place => place.placeId) : [];
    updateReward(index, {
      productId,
      productName: option?.name ?? null,
      places: await buildPlaces(reward, placeIds, productId),
    });
  };

  const updateReward = (index: number, patch: Partial<RewardValue>) =>
    onChange({ rewards: value.rewards.map((r, i) => (i === index ? { ...r, ...patch } : r)) });

  const removeReward = (index: number) =>
    onChange({ rewards: value.rewards.filter((_, i) => i !== index) });

  const updateRequirement = (patch: Partial<RequirementValue>) =>
    onChange({ requirement: { ...value.requirement, ...patch } });

  const optionsByIds = (options: Option[], ids: string[]) => options.filter(o => ids.includes(o.id));

  /**
   * O requisito numa frase só. É o que separa "um abastecimento de 30 L" de "30 L somando
   * vários" — nos dois selects, essa diferença some.
   */
  const requirementSentence = useMemo(() => {
    const { type, value: amount, valueType, fuelIds, placeIds } = value.requirement;
    const named = (options: Option[], ids: string[]) => optionsByIds(options, ids).map(o => o.name).join(', ');
    let quanto = __t('abastecer');
    if (amount !== null) {
      quanto = valueType === 'volume'
        ? __t('abastecer %{amount} L', { amount })
        : __t('abastecer R$ %{amount}', { amount });
    }
    return __t('O cliente precisa %{quanto} de %{combustiveis} em %{postos}, %{como}. O cupom vale %{horas} h.', {
      quanto,
      combustiveis: fuelIds.length ? named(fuels, fuelIds) : __t('qualquer combustível'),
      postos: placeIds.length ? named(places, placeIds) : __t('qualquer posto da rede'),
      como: type === 'accumulated_fillin' ? __t('somando vários abastecimentos') : __t('num abastecimento só'),
      horas: value.couponHours,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.requirement, value.couponHours, fuels, places]);

  return (
    <Stack spacing={3}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={SECTION_TITLE_SX}>Recompensas do pacote</Typography>
          {value.rewards.length > 0 && (
            <Chip size="small" variant="outlined" sx={COST_CHIP_SX} label={`Custo do pacote: R$ ${totalCost.toFixed(2).replace('.', ',')}`} />
          )}
          <Button size="small" variant="outlined" sx={{ ml: 'auto' }} startIcon={<AddIcon />} onClick={() => setTypeModalOpen(true)}>
            Adicionar recompensa
          </Button>
        </Box>

        {value.rewards.length === 0 && (
          <Typography variant="body2" color="warning.main">
            Adicione pelo menos uma recompensa.
          </Typography>
        )}

        {value.rewards.map((reward, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
              <Box sx={REWARD_ICON_SX}>{REWARD_ICON[reward.type]}</Box>
              {/* Constante de módulo com chave técnica: o extrator não enxerga os valores,
                  então a tradução acontece aqui e as 4 entradas moram no catalog/base.ts. */}
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{__t(REWARD_TYPE_LABEL[reward.type])}</Typography>
              <IconButton size="small" color="error" sx={{ ml: 'auto' }} onClick={() => removeReward(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {reward.type === 'virtual' && reward.cashValue === undefined && (
                <>
                  <LabeledInput
                    id={`${uid}-${index}-nome`} label="Nome do produto" width={320}
                    value={reward.productName || ''}
                    onChange={(e) => updateReward(index, { productName: e.target.value })}
                  />
                  <LabeledInput
                    id={`${uid}-${index}-desc`} label="Descrição (opcional)" width={320}
                    value={reward.productDescription || ''}
                    onChange={(e) => updateReward(index, { productDescription: e.target.value || null })}
                  />
                </>
              )}
              {reward.type === 'virtual' && reward.cashValue !== undefined && (
                <>
                  <LabeledInput
                    id={`${uid}-${index}-nome`} label="Nome do produto" width={320}
                    value={reward.productName || ''}
                    onChange={(e) => updateReward(index, { productName: e.target.value })}
                  />
                  <LabeledInput
                    id={`${uid}-${index}-cash`} label="Valor em dinheiro" prefix="R$" width={180}
                    inputMode="decimal"
                    value={reward.cashValue === null || reward.cashValue === undefined ? '' : String(reward.cashValue)}
                    onChange={(e) => updateReward(index, { cashValue: e.target.value === '' ? null : +e.target.value })}
                  />
                </>
              )}
              {reward.type === 'existing' && (
                // Busca no servidor: o catálogo de uma rede passa de cem itens, e uma lista
                // inteira carregada de antemão não é navegável nem rápida.
                <Field id={`${uid}-${index}-produto`} label="Produto do catálogo" width={640}>
                  <AsyncAutocomplete
                    resource="chains/chainId/products"
                    // Quem rotula é o <label> do Field; o rótulo flutuante do MUI desalinharia a linha.
                    label=""
                    filters={{ isDisabled: false }}
                    // o par {id, name} guardado no estado é o que o campo mostra sem ter de
                    // reconsultar — mesmo padrão do ExistingProductForm no /gifts
                    value={reward.productId ? { id: reward.productId, name: reward.productName || '' } : null}
                    getOptionLabel={(option: any) => option?.name || ''}
                    onChange={(option: any) => applyProduct(index, option ? { id: option.id, name: option.name } : null)}
                    renderOption={(props: any, option: any) => (
                      <li {...props} key={option.id}>
                        <Box display="flex" alignItems="center" gap={2} width="100%">
                          <ImageWithFallback src={option.imagePath} alt={option.name} width={40} height={40} borderRadius={4} />
                          <Box>
                            <Typography variant="body1">{option.name}</Typography>
                            {option.description && (
                              <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                                {option.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </li>
                    )}
                  />
                </Field>
              )}
              {reward.type === 'fuelDiscount' && (
                <Field id={`${uid}-${index}-desconto`} label="Desconto da recompensa" width={640}>
                  <Autocomplete
                    options={rewardDiscounts}
                    getOptionLabel={(o) => o.name}
                    value={rewardDiscounts.find(d => d.id === reward.discountId) || null}
                    onChange={(_, option) => updateReward(index, { discountId: option?.id ?? null })}
                    renderInput={(params) => <TextField {...params} id={`${uid}-${index}-desconto`} />}
                  />
                </Field>
              )}
              {reward.type === 'drawing_number' && (
                <>
                  <Field id={`${uid}-${index}-sorteio`} label="Sorteio" width={320}>
                    <Autocomplete
                      options={drawings}
                      getOptionLabel={(o) => o.name}
                      value={drawings.find(d => d.id === reward.drawingId) || null}
                      onChange={(_, option) => updateReward(index, { drawingId: option?.id ?? null })}
                      renderInput={(params) => <TextField {...params} id={`${uid}-${index}-sorteio`} />}
                    />
                  </Field>
                  <LabeledInput
                    id={`${uid}-${index}-numeros`} label="Quantos números" width={180} inputMode="numeric"
                    value={String(reward.luckyNumberAmount ?? 1)}
                    onChange={(e) => updateReward(index, { luckyNumberAmount: +e.target.value || 1 })}
                  />
                </>
              )}
            </Box>

            {rewardNeedsPlaces(reward) && (
              <Box sx={{ mt: 2 }}>
                <Field
                  id={`${uid}-${index}-locais`}
                  label="Locais de troca"
                  width={640}
                  hint={placesLocked(reward)
                    ? __t('Escolha o produto do catálogo primeiro: é dele que vem o valor de cada local.')
                    : __t('Onde o cliente retira esta recompensa — e quanto o posto recebe em cada um.')}
                >
                  <Autocomplete
                    multiple
                    disabled={placesLocked(reward)}
                    options={places}
                    getOptionLabel={(o) => o.name}
                    value={optionsByIds(places, reward.places.map(p => p.placeId))}
                    onChange={(_, options) => applyPlaces(index, options.map(o => o.id))}
                    renderInput={(params) => <TextField {...params} id={`${uid}-${index}-locais`} />}
                  />
                </Field>

                {reward.places.map((place, placeIndex) => (
                  <Paper key={place.placeId} variant="outlined"
                    sx={{ ...PLACE_ROW_SX, ...(place.hasProductPlace ? PLACE_AUTO_SX : null) }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: '1 1 200px', minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {places.find(o => o.id === place.placeId)?.name || place.placeId}
                      </Typography>
                      <Chip
                        size="small"
                        sx={{ alignSelf: ALIGN_START }}
                        icon={place.hasProductPlace ? <CheckCircleIcon /> : <WarningIcon />}
                        color={place.hasProductPlace ? 'success' : 'warning'}
                        label={place.hasProductPlace ? __t('Automático') : __t('Manual')}
                      />
                    </Box>
                    <Field
                      label="Valor do parceiro"
                      width={190}
                      hint={place.hasProductPlace
                        ? __t('Valor automático do catálogo de produtos')
                        : __t('O que o posto recebe por esta troca')}
                    >
                      <CurrencyInput
                        value={place.partnerValue ?? 0}
                        disabled={place.hasProductPlace}
                        fullWidth
                        onChange={(v: number) => updateReward(index, {
                          places: reward.places.map((p, i) => (i === placeIndex ? { ...p, partnerValue: v } : p)),
                        })}
                      />
                    </Field>
                  </Paper>
                ))}
              </Box>
            )}
          </Paper>
        ))}
      </Box>

      <Box sx={SECTION_SX}>
        <Typography sx={SECTION_TITLE_SX}>O que o cliente precisa fazer</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
        <Field id={`${uid}-requisito`} label="Requisito" width={320}>
          <TextField
            select id={`${uid}-requisito`} fullWidth
            value={value.requirement.value === null ? 'any' : value.requirement.type}
            onChange={(e) => updateRequirement(e.target.value === 'any'
              ? { type: 'fillin', value: null }
              : { type: e.target.value as RequirementValue['type'], value: value.requirement.value ?? 20 })}
          >
            <MenuItem value="any">Qualquer abastecimento</MenuItem>
            <MenuItem value="fillin">Um abastecimento de pelo menos…</MenuItem>
            <MenuItem value="accumulated_fillin">Acumular…</MenuItem>
          </TextField>
        </Field>
        {value.requirement.value !== null && (
          <>
            <LabeledInput
              id={`${uid}-req-valor`} label="Valor" width={130} inputMode="decimal"
              value={String(value.requirement.value)}
              onChange={(e) => updateRequirement({ value: +e.target.value || null })}
            />
            {/* Duas opções: alternador em vez de select, que esconde metade da escolha. */}
            <Box sx={SEG_COLUMN_SX}>
              <Box component="span" sx={FIELD_LABEL_SX}>Unidade</Box>
              <Seg
                value={value.requirement.valueType}
                onChange={(unit) => updateRequirement({ valueType: unit })}
                tone="brand"
                label={__t('Unidade do requisito')}
                options={[{ value: 'volume', label: 'Litros' }, { value: 'cents', label: 'Reais' }]}
              />
            </Box>
          </>
        )}
        <LabeledInput
          id={`${uid}-cupom-h`} label="Cupom vale" suffix="h" width={150} inputMode="numeric"
          value={String(value.couponHours)}
          onChange={(e) => onChange({ couponHours: +e.target.value || 1 })}
        />
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Field id={`${uid}-combustiveis`} label="Combustíveis" width={320} hint="Vazio = qualquer um">
          <Autocomplete
            multiple options={fuels} getOptionLabel={(o) => o.name}
            value={optionsByIds(fuels, value.requirement.fuelIds)}
            onChange={(_, options) => updateRequirement({ fuelIds: options.map(o => o.id) })}
            renderInput={(params) => <TextField {...params} id={`${uid}-combustiveis`} />}
          />
        </Field>
        <Field id={`${uid}-postos`} label="Postos onde vale abastecer" width={320} hint="Vazio = qualquer um">
          <Autocomplete
            multiple options={places} getOptionLabel={(o) => o.name}
            value={optionsByIds(places, value.requirement.placeIds)}
            onChange={(_, options) => updateRequirement({ placeIds: options.map(o => o.id) })}
            renderInput={(params) => <TextField {...params} id={`${uid}-postos`} />}
          />
        </Field>
      </Box>

      <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={SENTENCE_SX}>
        {requirementSentence}
      </Alert>

      <ProductTypeSelectionModal
        open={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        onSelectType={addReward}
        excludedTypes={EXCLUDED_PRODUCT_TYPES}
      />
    </Stack>
  );
};

export default LevelRewardEditor;
